import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	SquareCheck,
	Repeat,
	MessageSquare,
	MessagesSquare,
	ChevronRight,
	Check,
	CheckCheck,
	ShieldCheck
} from 'lucide-react';
import moment from 'moment';
import T from 'Components/Helpers/translate';
import AssistantResource, { type InboxItem, type InboxUpgradeRequest } from 'Resources/AssistantChat';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import { inboxCountStore } from 'App/pages/FloatingAIChat/useInboxCount';
import { notifyAIWorkspaceError } from '../notifyAIWorkspaceError';
import { renderMarkdown } from 'Helpers/markdown';
import { buildPath } from '../useWorkspaceNav';
import InboxProposalCard, { INBOX_CARD_ACTIONS } from './InboxProposalCard';
import { actionLabelKey, capabilityLabel, entityLabel } from '../AgentConfig/autonomyGrants';

type Props = {
	currentConversationId: number | null;
	/** Open the conversation an inbox row belongs to. */
	onSelectConversation: (conversationId: number) => void;
	refreshKey?: number;
};

const ROUTINE_CONTEXTS = new Set(['routine', 'scheduled', 'event_hook']);

/** Strip markdown to a single plain-text line for the row preview. */
const previewText = (md: string | null): string => {
	if (!md) return '';
	return md
		.replace(/```[\s\S]*?```/g, ' ')
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		.replace(/^\s*[-+*]\s+/gm, '')
		.replace(/^\s*\d+\.\s+/gm, '')
		.replace(/[*_`#>]/g, '')
		.replace(/\s+/g, ' ')
		.trim();
};

/** Today → time, this week → weekday, older → day + month. */
const formatWhen = (date: string): string => {
	const m = moment(date);
	const now = moment();
	if (m.isSame(now, 'day')) return m.format('LT');
	if (m.isSame(now, 'week')) return m.format('ddd');
	return m.format('D MMM');
};

type RowDescriptor = {
	tile: 'proposal' | 'routine' | 'message' | 'permission';
	Icon: typeof MessageSquare;
	/** Proposals carry a pill; the icon tile conveys routine vs message. */
	tag: string | null;
	title: string;
	sub: string;
};

/**
 * What the agent is asking to be allowed to do, in the rep's words — "create
 * contacts", "send email". Falls back to a generic line when the payload isn't a
 * shape we recognise, so an odd row still reads as a permission request.
 */
const describeUpgrade = (upgrade: InboxUpgradeRequest | null | undefined): string => {
	if (upgrade?.type === 'entity') {
		// Shared with the Permissions tab so the two name a grant the same way, and
		// so an upgrade on an alias key reads as `account` rather than the lang key.
		const action = T(actionLabelKey(upgrade.action));
		return T('assistant.inbox.upgrade.entitySub', { action, entity: entityLabel(upgrade.entity) });
	}
	if (upgrade?.type === 'capability') {
		return T('assistant.inbox.upgrade.capabilitySub', { capability: capabilityLabel(upgrade.capability) });
	}
	return T('assistant.inbox.upgrade.genericSub');
};

/**
 * What a proposal row says it is waiting for. `action` is the approval's tool
 * name as the engine wrote it (`crmMutation`, `scrapeContacts`, …), so the key
 * is derived rather than listed — a tool we add later falls back to the generic
 * line instead of rendering its own key at the user (WEB-11473).
 */
const actionLabel = (action: string): string => {
	const key = `assistant.inbox.action.${action}`;
	const translated = T(key);
	return translated && translated !== key ? translated : T('assistant.inbox.actionFallback');
};

/**
 * WEB-6799 — the Inbox page. A single compact feed grouped into "Needs you"
 * (pending proposals), "Unread" (unseen agent activity) and "Earlier" (already
 * seen, dimmed). Each row carries a type tag, a one-line preview, an unread dot
 * and a timestamp, and opens its conversation on click.
 */
const WorkspaceInbox = ({ currentConversationId, onSelectConversation, refreshKey }: Props) => {
	const [items, setItems] = useState<InboxItem[]>([]);
	const [loading, setLoading] = useState(true);
	/**
	 * Which rows are open. A conversation id alone is not enough since
	 * WEB-11464: one conversation can carry several proposals, and they open
	 * one at a time — so the key says which of the two a row is (`rowKey`).
	 */
	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	const [earlierExpanded, setEarlierExpanded] = useState(false);
	/** Approval ids with a decision in flight — disables that row's buttons. */
	const [resolving, setResolving] = useState<Set<number>>(new Set());

	// Monotonic token shared by every load (the effect and the mark handlers).
	// Only the most recent load applies its result; unmount bumps it so a
	// late-resolving getInbox can't setState on an unmounted component or
	// clobber a newer list.
	const loadToken = useRef(0);
	useEffect(() => () => void (loadToken.current += 1), []);

	// `autoExpand` opens the latest unread up front — only wanted on the initial
	// page load, not on the silent refreshes that follow a mark-as-read (those
	// must leave the user's current expand state alone).
	const load = useCallback((opts?: { autoExpand?: boolean }) => {
		const token = (loadToken.current += 1);
		const autoExpand = opts?.autoExpand ?? false;
		const current = () => token === loadToken.current;
		setLoading(true);
		AssistantResource.getInbox()
			.then(rows => {
				if (!current()) return;
				setItems(rows);
				// Keep the floating-chat badge in sync from the rows we just
				// fetched, so a mark-as-read doesn't need a second getInbox call.
				inboxCountStore.setFromItems(rows);
				if (autoExpand) {
					setEarlierExpanded(false);
					const firstUnread = rows.find(i => i.kind === 'unread');
					setExpanded(
						firstUnread ? new Set([`conv-${firstUnread.conversationId ?? firstUnread.id}`]) : new Set()
					);
				}
			})
			.catch(err => {
				notifyAIWorkspaceError(err, 'getInbox');
				if (current()) setItems([]);
			})
			.finally(() => {
				if (current()) setLoading(false);
			});
	}, []);

	useEffect(() => load({ autoExpand: true }), [refreshKey, load]);

	// Mark handlers reload the feed (which also re-syncs the badge, see load);
	// the marked row leaves "Unread" and moves to "Earlier".
	const markRead = (conversationId: number) =>
		AssistantResource.markInboxRead(conversationId)
			.then(() => load())
			.catch(err => notifyAIWorkspaceError(err, 'markInboxRead'));

	// "Mark all" clears exactly the Unread rows the user currently sees; passing
	// their ids keeps approval-hidden conversations out of the sweep.
	const markAllRead = () => {
		const unreadIds = items
			.filter(i => i.kind === 'unread')
			.map(i => i.conversationId)
			.filter((id): id is number => id != null);
		return AssistantResource.markAllInboxRead(unreadIds)
			.then(() => load())
			.catch(err => notifyAIWorkspaceError(err, 'markAllInboxRead'));
	};

	// Permission upgrades are the one approval kind resolved from the Inbox itself:
	// they carry no conversation, so there's no card elsewhere to send the user to.
	// `skipContinuation` because there's no run to resume — the request is raised by
	// the upgrade tracker, not by an agent turn.
	const resolveUpgrade = (approvalId: number, decision: 'approve' | 'decline') => {
		setResolving(prev => new Set(prev).add(approvalId));
		const request =
			decision === 'approve'
				? AgentApprovalsResource.approve(approvalId, undefined, undefined, { skipContinuation: true })
				: AgentApprovalsResource.reject(
						approvalId,
						T('assistant.inbox.upgrade.declinedResolution'),
						undefined,
						{ skipContinuation: true }
				  );
		return request
			.then(() => load())
			.catch(err => notifyAIWorkspaceError(err, 'resolvePermissionUpgrade'))
			.finally(() =>
				setResolving(prev => {
					const next = new Set(prev);
					next.delete(approvalId);
					return next;
				})
			);
	};

	/**
	 * What a row is, not which group it currently sits in: marking an open
	 * unread row as read moves it to "Earlier", and keying on `kind` would
	 * change its key and collapse it under the person reading it.
	 */
	const rowKey = (item: InboxItem) =>
		item.kind === 'approval' ? `approval-${item.id}` : `conv-${item.conversationId ?? item.id}`;

	const toggle = (key: string) =>
		setExpanded(prev => {
			const next = new Set(prev);
			if (next.has(key)) next.delete(key);
			else next.add(key);
			return next;
		});

	const describe = (item: InboxItem): RowDescriptor => {
		if (item.kind === 'approval' && item.action === 'permissionUpgrade') {
			// Resolved inline below — this row has no conversation to open.
			return {
				tile: 'permission',
				Icon: ShieldCheck,
				tag: T('assistant.inbox.tag.permission') || 'Permission',
				title: item.title
					? T('assistant.inbox.upgrade.title', { agent: item.title })
					: T('assistant.inbox.upgrade.titleFallback'),
				// WEB-11464: say what kind of yes this is. A permission holds until
				// it is taken back; the proposal below says "this once". Both were
				// called "approve", which is why clearing the permission rows read
				// as clearing the queue.
				sub: `${describeUpgrade(item.upgrade)} — ${T('assistant.inbox.upgrade.scope')}`
			};
		}
		if (item.kind === 'approval') {
			return {
				tile: 'proposal',
				Icon: SquareCheck,
				tag: T('assistant.inbox.tag.proposal') || 'Proposal',
				title: item.title || T('assistant.chat'),
				// `T` hands back the key itself when there is no entry, so the `||`
				// chain never reaches the fallback — an action without a key renders
				// as `assistant.inbox.action.crmMutation` in front of the user. Compare
				// against the key to tell "missing" from "translated to something".
				sub: `${actionLabel(item.action)} — ${T('assistant.inbox.proposal.scope')}`
			};
		}
		const isRoutine = !!item.contextType && ROUTINE_CONTEXTS.has(item.contextType);
		return {
			tile: isRoutine ? 'routine' : 'message',
			Icon: isRoutine ? Repeat : MessageSquare,
			tag: null,
			title: item.title || T('assistant.chat'),
			sub: previewText(item.lastMessage)
		};
	};

	const renderRow = (item: InboxItem, showDot: boolean, dimmed: boolean) => {
		const { tile, Icon, tag, title, sub } = describe(item);
		const isUpgrade = item.kind === 'approval' && item.action === 'permissionUpgrade';
		const conversationId = item.conversationId ?? null;
		const isActive = conversationId != null && conversationId === currentConversationId;
		// Conversation rows (unread/earlier) expand to show the full message.
		// Proposals have no message body — a click just opens the conversation.
		// WEB-11464: a proposal expands to its card and is decided in place. Its
		// state is keyed by approval id, not conversation id.
		// Only the actions that have a card — the rest (sendUserEmail, webSearch)
		// have none in the chat either, so their row keeps opening the thread.
		const proposal =
			item.kind === 'approval' && !isUpgrade && item.approval && INBOX_CARD_ACTIONS.has(item.action)
				? item.approval
				: null;
		const key = rowKey(item);
		const expandable = item.kind === 'unread' || item.kind === 'earlier' || proposal != null;
		const isOpen = expandable && expanded.has(key);
		const openLabel = T('assistant.inbox.openConversation') || 'Open conversation';
		const convHref =
			conversationId != null ? `#${buildPath({ route: 'chat', agentRoute: null, conversationId })}` : null;
		const openConversation = (e: React.MouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			if (conversationId != null) onSelectConversation(conversationId);
		};
		const onRowActivate = () => {
			if (expandable) {
				toggle(key);
				return;
			}
			if (conversationId != null) onSelectConversation(conversationId);
		};
		return (
			<div
				key={`${item.kind}-${item.id}`}
				className={`ws-inbox-item ${dimmed ? 'ws-inbox-item--earlier' : ''} ${isActive ? 'active' : ''}`}
			>
				<div
					// An upgrade row has nothing to open or expand — its buttons are the
					// only controls, so it must not present itself as clickable.
					className={`ws-inbox-row ${isUpgrade ? 'ws-inbox-row--static' : ''}`}
					role={isUpgrade ? undefined : 'button'}
					tabIndex={isUpgrade ? undefined : 0}
					aria-expanded={expandable ? isOpen : undefined}
					onClick={onRowActivate}
					onKeyDown={e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							onRowActivate();
						}
					}}
				>
					<span className={`ws-inbox-icon ws-inbox-icon--${tile}`}>
						<Icon style={{ width: 16, height: 16 }} />
					</span>
					<span className="ws-inbox-row-main">
						<span className="ws-inbox-row-head">
							<span className="ws-inbox-row-title">{title}</span>
							{tag && <span className={`ws-inbox-tag ws-inbox-tag--${tile}`}>{tag}</span>}
							{/* Real link to the thread — distinct from expanding the row. */}
							{convHref && (
								<a
									className="ws-inbox-link"
									href={convHref}
									onClick={openConversation}
									title={openLabel}
									aria-label={openLabel}
								>
									<MessagesSquare style={{ width: 14, height: 14 }} />
									{/* Wrapped so a narrow row can drop to the icon alone — see
									    the ws-inbox-head container query in styles/history.ts. */}
									<span className="ws-inbox-link-label">
										{T('assistant.inbox.conversation') || 'Conversation'}
									</span>
								</a>
							)}
						</span>
						{sub && !isOpen && <span className="ws-inbox-row-preview">{sub}</span>}
					</span>
					{/* Fixed-width slots so the time/dot/chevron columns line up down
					    the list and the header link always sits at the same x. */}
					<span className="ws-inbox-meta">
						{isUpgrade && (
							<span className="ws-inbox-decide">
								<button
									type="button"
									className="ws-inbox-decide-btn ws-inbox-decide-btn--deny"
									disabled={resolving.has(item.id)}
									onClick={e => {
										e.preventDefault();
										e.stopPropagation();
										resolveUpgrade(item.id, 'decline');
									}}
								>
									{T('assistant.inbox.upgrade.decline')}
								</button>
								<button
									type="button"
									className="ws-inbox-decide-btn ws-inbox-decide-btn--allow"
									disabled={resolving.has(item.id)}
									onClick={e => {
										e.preventDefault();
										e.stopPropagation();
										resolveUpgrade(item.id, 'approve');
									}}
								>
									{T('assistant.inbox.upgrade.allow')}
								</button>
							</span>
						)}
						{item.kind === 'unread' && conversationId != null && (
							<button
								type="button"
								className="ws-inbox-mark"
								title={T('assistant.inbox.markRead') || 'Mark as read'}
								aria-label={T('assistant.inbox.markRead') || 'Mark as read'}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									markRead(conversationId);
								}}
							>
								<Check style={{ width: 15, height: 15 }} />
							</button>
						)}
						<span className="ws-inbox-dot-slot">{showDot && <span className="ws-inbox-dot" />}</span>
						<span className="ws-inbox-when">{item.regDate ? formatWhen(item.regDate) : ''}</span>
						<span className="ws-inbox-chev-slot">
							{expandable && (
								<span className={`ws-sb-chev ${isOpen ? 'open' : ''}`}>
									<ChevronRight style={{ width: 14, height: 14 }} />
								</span>
							)}
						</span>
					</span>
				</div>
				{isOpen && (
					<div className="ws-inbox-item-body">
						{proposal ? (
							<InboxProposalCard approval={proposal} onResolved={load} />
						) : (
							<div
								className="pr-prose ws-inbox-message"
								dangerouslySetInnerHTML={{
									__html: renderMarkdown(
										(item.kind === 'unread' || item.kind === 'earlier' ? item.lastMessage : null) ||
											T('assistant.inbox.noPreview')
									)
								}}
							/>
						)}
					</div>
				)}
			</div>
		);
	};

	const approvals = items.filter((i): i is Extract<InboxItem, { kind: 'approval' }> => i.kind === 'approval');
	const unread = items.filter(i => i.kind === 'unread');
	const earlier = items.filter(i => i.kind === 'earlier');
	const hasItems = items.length > 0;
	const EARLIER_COLLAPSED = 5;
	const visibleEarlier = earlierExpanded ? earlier : earlier.slice(0, EARLIER_COLLAPSED);
	const hiddenEarlier = earlier.length - EARLIER_COLLAPSED;

	const section = (
		labelKey: string,
		fallback: string,
		rows: InboxItem[],
		opts: { dot: boolean; dim: boolean; count: boolean; action?: React.ReactNode }
	) =>
		rows.length > 0 && (
			<div className="ws-inbox-section">
				<div className={`ws-history-group-label ${opts.action ? 'ws-inbox-group-label--action' : ''}`}>
					<span>
						{T(labelKey) || fallback}
						{opts.count && <span className="ws-inbox-section-count">{rows.length}</span>}
					</span>
					{opts.action}
				</div>
				<div className="ws-inbox-list">{rows.map(r => renderRow(r, opts.dot, opts.dim))}</div>
			</div>
		);

	return (
		<div className="ws-history">
			<div className="ws-history-inner">
				<h1 className="ws-history-title">{T('assistant.inbox') || 'Inbox'}</h1>
				{!loading && !hasItems && (
					<div className="ws-history-empty">{T('assistant.inbox.empty') || 'All caught up'}</div>
				)}
				{section('assistant.inbox.group.needsYou', 'Needs you', approvals, {
					dot: true,
					dim: false,
					count: true
				})}
				{section('assistant.inbox.group.unread', 'Unread', unread, {
					dot: true,
					dim: false,
					count: true,
					action: (
						<button type="button" className="ws-inbox-markall-link" onClick={markAllRead}>
							<CheckCheck style={{ width: 14, height: 14 }} />
							{T('assistant.inbox.markAllRead') || 'Mark all as read'}
						</button>
					)
				})}
				{earlier.length > 0 && (
					<div className="ws-inbox-section ws-inbox-section--spaced">
						<div className="ws-history-group-label">{T('assistant.inbox.group.earlier') || 'Earlier'}</div>
						<div className="ws-inbox-list">{visibleEarlier.map(r => renderRow(r, false, true))}</div>
						{!earlierExpanded && hiddenEarlier > 0 && (
							<button
								type="button"
								className="ws-inbox-showmore"
								onClick={() => setEarlierExpanded(true)}
							>
								{T('assistant.inbox.showMore', { count: hiddenEarlier }) ||
									`Show ${hiddenEarlier} more`}
							</button>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default WorkspaceInbox;
